import { ContractTradeType, CreateOrderDropdownValue } from "@/types";
import { useTranslation } from "react-i18next";
import { Grid, Stack, Divider } from "@mantine/core";
import { getPaymentDirection } from "@/utils/Contracts";
import Text from "@/components/atoms/Text";
import NumberFormatter from "@/components/atoms/NumberFormatter";

interface Props {
  contractType: ContractTradeType;
  isPaymentReceiverInverted: boolean;
  agreedBuyerPricePrUnit: string;
  agreedSellerPricePrUnit: string;
  currentCompanyName: string;
  tradingPartnerCompanyName: string;
  unitOptions: CreateOrderDropdownValue[];
  priceUnitId: number;
  currencyType: string;
  indexRegulated: boolean;
  indexRegulationPercentage: number;
}

const BuyerAndSellerPriceLabels = ({
  contractType,
  isPaymentReceiverInverted,
  agreedBuyerPricePrUnit,
  agreedSellerPricePrUnit,
  currentCompanyName,
  tradingPartnerCompanyName,
  unitOptions,
  priceUnitId,
  currencyType,
  indexRegulated,
  indexRegulationPercentage,
}: Props) => {
  const { t } = useTranslation();

  // Same rule as the counterparty's own view, so the two always agree on who pays whom.
  const currentCompanyReceives = getPaymentDirection(contractType, isPaymentReceiverInverted) === "receives";
  // Each party gets its own side's price: the seller's price to the seller, the buyer's price to the buyer.
  const receiverPrice = isPaymentReceiverInverted ? agreedBuyerPricePrUnit : agreedSellerPricePrUnit;
  const payerPrice = isPaymentReceiverInverted ? agreedSellerPricePrUnit : agreedBuyerPricePrUnit;
  const receiverName = currentCompanyReceives ? currentCompanyName : tradingPartnerCompanyName;
  const payerName = currentCompanyReceives ? tradingPartnerCompanyName : currentCompanyName;
  const receiverLabel = `${receiverName} ${t("contracts:switchLabelPayment.receives")}`;
  const payerLabel = `${payerName} ${t("contracts:switchLabelPayment.pays")}`;

  const priceUnit = unitOptions.find((unit) => unit.id === priceUnitId)?.value;
  const nextYearMultiplier = 1 + indexRegulationPercentage / 100;
  return (
    <>
      <Grid.Col span={6}>
        <Stack>
          <Text data-testid={"receiver-label"} size="sm" color="secondary">
            {receiverLabel}
          </Text>
          <Text>
            <NumberFormatter
              data-testid={"receiver-price"}
              value={receiverPrice ? receiverPrice : "0.00"}
              suffix={` ${currencyType} per ${priceUnit}`}
            />
          </Text>
          <Divider />
        </Stack>
      </Grid.Col>

      <Grid.Col span={6}>
        <Stack>
          <Text size="sm" color="secondary" data-testid={"payer-label"}>
            {payerLabel}
          </Text>
          <Text>
            <NumberFormatter
              value={payerPrice ? payerPrice : "0.00"}
              suffix={` ${currencyType} per ${priceUnit}`}
              data-testid={"payer-price"}
            />
          </Text>
          <Divider />
        </Stack>
      </Grid.Col>
      {indexRegulated && (
        <>
          <Grid.Col span={12}>
            <Text size="sm" color="secondary">
              {t("contracts:yearlyIncreaseIndexRegulation") + ` ${indexRegulationPercentage}%`}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Stack>
              <Text>
                <NumberFormatter
                  value={receiverPrice ? (Number(receiverPrice) * nextYearMultiplier).toFixed(2) : "0.00"}
                  suffix={` ${currencyType} per ${priceUnit}`}
                />{" "}
              </Text>
              <Divider />
            </Stack>
          </Grid.Col>
          <Grid.Col span={6}>
            <Stack>
              <Text>
                <NumberFormatter
                  value={payerPrice ? (Number(payerPrice) * nextYearMultiplier).toFixed(2) : "0.00"}
                  suffix={` ${currencyType} per ${priceUnit}`}
                />{" "}
              </Text>
              <Divider />
            </Stack>
          </Grid.Col>
        </>
      )}
    </>
  );
};

export default BuyerAndSellerPriceLabels;
